import Phaser from 'phaser';

import { DungeonScene } from './dungeon.scene';
import { type WorldView } from './world-view';

export interface GameMount {
  readonly parent: HTMLElement;
  readonly width: number;
  readonly height: number;
  /**
   * Where every entity is, where a click goes, and which floor is being drawn.
   *
   * The cave used to be handed over beside this and no longer is. A descent
   * replaces it mid-session, and a scene constructed with one holds the one it
   * was constructed with — `scene.restart()` does not run a constructor. So the
   * floor is asked for, once a frame, through the same port everything else
   * about the world comes through (`./world-view`).
   */
  readonly view: WorldView;
}

/** Background of the world beyond the cave. Dark, because this is a dungeon crawler. */
const BACKGROUND = '#0b0b0d';

/**
 * Builds the Phaser game. Kept as a plain function taking its mount point so
 * the React layer owns the lifecycle and nothing here reaches for the DOM by id.
 *
 * The scene is handed a finished cave and a view as a constructed instance
 * rather than fetching either itself: a scene that knew how to find its own
 * level would be a scene with a rule in it, and a scene that knew how to open a
 * socket would be a renderer with a network stack in it.
 */
export const createGame = ({ parent, width, height, view }: GameMount): Phaser.Game =>
  new Phaser.Game({
    type: Phaser.AUTO,
    parent,
    width,
    height,
    backgroundColor: BACKGROUND,
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
    scene: [new DungeonScene({ view })],
  });
